import { create } from 'zustand';
import { ethers } from 'ethers';

interface WalletState {
  address: string | null;
  balance: string;
  chainId: number | null;
  isConnected: boolean;
  isConnecting: boolean;
  provider: ethers.BrowserProvider | null;
  signer: ethers.JsonRpcSigner | null;
}

interface PredictionState {
  symbol: string;
  interval: string;
  predictionLength: number;
  predictions: any[];
  isLoading: boolean;
  error: string | null;
  lastQueryId: string | null;
}

interface PaymentState {
  isPaying: boolean;
  paymentStatus: 'idle' | 'pending' | 'success' | 'failed';
  paymentError: string | null;
  lastPaymentHash: string | null;
}

interface AppStore {
  // 钱包状态
  wallet: WalletState;
  connectWallet: () => Promise<void>;
  disconnectWallet: () => void;
  updateBalance: () => Promise<void>;

  // 预测状态
  prediction: PredictionState;
  setSymbol: (symbol: string) => void;
  setInterval: (interval: string) => void;
  setPredictionLength: (length: number) => void;
  setPredictions: (predictions: any[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // 支付状态
  payment: PaymentState;
  setPaymentStatus: (status: PaymentState['paymentStatus']) => void;
  setPaymentError: (error: string | null) => void;
  resetPayment: () => void;
}

const MONAD_TESTNET_CHAIN_ID = 10143;
const MONAD_TESTNET_RPC = 'https://testnet-rpc.monad.xyz';

// 统一获取 EVM 钱包（MetaMask / OKX Wallet）提供者
function getEvmProvider(): { rawProvider: any; walletName: string } {
  const w = window as any;

  // OKX Wallet 注入方式兼容
  if (w.okxwallet?.ethereum) {
    return { rawProvider: w.okxwallet.ethereum, walletName: 'OKX Wallet' };
  }
  if (w.okxwallet) {
    return { rawProvider: w.okxwallet, walletName: 'OKX Wallet' };
  }
  if (w.ethereum?.isOkxWallet) {
    return { rawProvider: w.ethereum, walletName: 'OKX Wallet' };
  }

  // MetaMask 或其他兼容 EIP-1193 的钱包
  if (w.ethereum) {
    return { rawProvider: w.ethereum, walletName: 'MetaMask / EVM Wallet' };
  }

  throw new Error('请先安装 MetaMask 或 OKX Wallet 钱包插件');
}

export const useStore = create<AppStore>((set, get) => ({
  // 钱包初始状态
  wallet: {
    address: null,
    balance: '0',
    chainId: null,
    isConnected: false,
    isConnecting: false,
    provider: null,
    signer: null,
  },

  // 连接钱包（兼容 MetaMask + OKX Wallet）
  connectWallet: async () => {
    set(state => ({
      wallet: { ...state.wallet, isConnecting: true },
    }));

    try {
      const { rawProvider } = getEvmProvider();
      const provider = new ethers.BrowserProvider(rawProvider);
      
      // 请求连接账户
      const accounts = await provider.send('eth_requestAccounts', []);
      
      if (accounts.length === 0) {
        throw new Error('未获取到账户');
      }

      // 检查并切换到 Monad 测试网
      const network = await provider.getNetwork();
      if (Number(network.chainId) !== MONAD_TESTNET_CHAIN_ID) {
        try {
          await rawProvider.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: `0x${MONAD_TESTNET_CHAIN_ID.toString(16)}` }],
          });
        } catch (switchError: any) {
          // 如果网络不存在，添加网络
          if (switchError.code === 4902) {
            await rawProvider.request({
              method: 'wallet_addEthereumChain',
              params: [{
                chainId: `0x${MONAD_TESTNET_CHAIN_ID.toString(16)}`,
                chainName: 'Monad Testnet',
                nativeCurrency: {
                  name: 'MON',
                  symbol: 'MON',
                  decimals: 18,
                },
                rpcUrls: [MONAD_TESTNET_RPC],
                blockExplorerUrls: ['https://testnet.monadexplorer.com'],
              }],
            });
          } else {
            throw switchError;
          }
        }
      }

      const signer = await provider.getSigner();
      const address = await signer.getAddress();
      const balance = await provider.getBalance(address);

      set({
        wallet: {
          address,
          balance: ethers.formatEther(balance),
          chainId: MONAD_TESTNET_CHAIN_ID,
          isConnected: true,
          isConnecting: false,
          provider,
          signer,
        },
      });

      // 监听账户变化
      rawProvider.on('accountsChanged', (accounts: string[]) => {
        if (accounts.length === 0) {
          get().disconnectWallet();
        } else {
          set(state => ({
            wallet: { ...state.wallet, address: accounts[0] },
          }));
          get().updateBalance();
        }
      });

      // 监听网络变化
      rawProvider.on('chainChanged', () => {
        window.location.reload();
      });

    } catch (error) {
      set(state => ({
        wallet: { ...state.wallet, isConnecting: false },
      }));
      throw error;
    }
  },

  // 断开钱包
  disconnectWallet: () => {
    set({
      wallet: {
        address: null,
        balance: '0',
        chainId: null,
        isConnected: false,
        isConnecting: false,
        provider: null,
        signer: null,
      },
    });
  },

  // 更新余额
  updateBalance: async () => {
    const { wallet } = get();
    if (!wallet.provider || !wallet.address) return;

    try {
      const balance = await wallet.provider.getBalance(wallet.address);
      set(state => ({
        wallet: { ...state.wallet, balance: ethers.formatEther(balance) },
      }));
    } catch (error) {
      console.error('更新余额失败:', error);
    }
  },

  // 预测初始状态
  prediction: {
    symbol: 'BTC',
    interval: '1h',
    predictionLength: 24,
    predictions: [],
    isLoading: false,
    error: null,
    lastQueryId: null,
  },

  setSymbol: (symbol) => set(state => ({
    prediction: { ...state.prediction, symbol },
  })),

  setInterval: (interval) => set(state => ({
    prediction: { ...state.prediction, interval },
  })),

  setPredictionLength: (predictionLength) => set(state => ({
    prediction: { ...state.prediction, predictionLength },
  })),

  setPredictions: (predictions) => set(state => ({
    prediction: { ...state.prediction, predictions },
  })),

  setLoading: (isLoading) => set(state => ({
    prediction: { ...state.prediction, isLoading },
  })),

  setError: (error) => set(state => ({
    prediction: { ...state.prediction, error },
  })),

  // 支付初始状态
  payment: {
    isPaying: false,
    paymentStatus: 'idle',
    paymentError: null,
    lastPaymentHash: null,
  },

  setPaymentStatus: (paymentStatus) => set(state => ({
    payment: { ...state.payment, paymentStatus, isPaying: paymentStatus === 'pending' },
  })),

  setPaymentError: (paymentError) => set(state => ({
    payment: { ...state.payment, paymentError, paymentStatus: 'failed' },
  })),

  resetPayment: () => set({
    payment: {
      isPaying: false,
      paymentStatus: 'idle',
      paymentError: null,
      lastPaymentHash: null,
    },
  }),
}));

// 类型声明
declare global {
  interface Window {
    ethereum?: any;
    okxwallet?: any;
  }
}

export default useStore;

